import React, { useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useStyles } from './ExecutionSideBar.styles';
import { useTheme } from '@mui/material/styles';
import { ThemeProps } from '@styles/theme';
import { RoutePaths } from '@core/routes/RoutePaths';

import BackIcon from '@assets/left-icon.svg';
import DownIcon from '@assets/down-icon.svg';
import UpIcon from '@assets/up-icon.svg';
import VideoIcon from '@assets/video.svg';
import ImprovementIcon from '@assets/edit.svg';

import {
    useExecutionsUIStore,
    useUserUIStore,
    useExecutionsGridUIStore,
    useGradingsUIStore,
} from '@core/useStores';

import { Observer } from 'mobx-react';
import { IUserStore } from 'Users/stores';
import { getDurationSeconds, getVideoDurationSeconds, VideoPlayer } from '@shared/components/VideoPlayer/VideoPlayer';
import { getExecutionStatusMeta } from 'NeoAnalytics/executionSteps';
import { getGradingStatusMeta } from 'NeoAnalytics/gradingStatus';
import { useResolvedMediaUrl } from 'NeoAnalytics/hooks/useResolvedMediaUrl';

const formatAdditionalDataLabel = (key: string) =>
    key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, character => character.toUpperCase());

const normalizeAdditionalDataKey = (value: string) => value.replace(/[^a-z0-9]/gi, '').toLowerCase();

const getPlainObjectEntries = (value: unknown, path = ''): Array<[string, string]> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return [];
    }

    return Object.entries(value as Record<string, unknown>).flatMap(([key, rawValue]) => {
        const nextPath = path ? `${path}.${key}` : key;

        if (rawValue == null) {
            return [];
        }

        if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
            return [[nextPath, String(rawValue)]];
        }

        return getPlainObjectEntries(rawValue, nextPath);
    });
};

const findAdditionalDataValue = (value: unknown, candidates: string[]) => {
    const normalizedCandidates = new Set(candidates.map(normalizeAdditionalDataKey));

    return getPlainObjectEntries(value).find(([key]) => normalizedCandidates.has(normalizeAdditionalDataKey(key)))?.[1] ?? null;
};

const getExecutionUserName = (additionalData: unknown) => {
    const explicitName = findAdditionalDataValue(additionalData, [
        'fullName',
        'displayName',
        'userName',
        'username',
        'actorName',
        'actorDisplayName',
        'traineeName',
        'participantName',
        'name',
    ]);

    if (explicitName) {
        return explicitName;
    }

    const firstName = findAdditionalDataValue(additionalData, [
        'firstName',
        'userFirstName',
        'actorFirstName',
        'givenName',
        'participantFirstName',
    ]);
    const lastName = findAdditionalDataValue(additionalData, [
        'lastName',
        'userLastName',
        'actorLastName',
        'surname',
        'familyName',
        'participantLastName',
    ]);

    return [firstName, lastName].filter(Boolean).join(' ').trim() || null;
};

const getExecutionUserRole = (additionalData: unknown) => findAdditionalDataValue(additionalData, [
    'role',
    'roleName',
    'userRole',
    'userRoleName',
    'actorRole',
    'actorRoleName',
    'participantRole',
    'jobRole',
]);

type ForwardedExecutionActor = {
    actorId?: string | null;
    displayName?: string | null;
    roleName?: string | null;
    email?: string | null;
};

export const ExecutionSideBar: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { id } = params;

    const userUIStore = useUserUIStore();
    const executionsGridUIStore = useExecutionsGridUIStore();
    const executionsUIStore = useExecutionsUIStore();
    const gradingsUIStore = useGradingsUIStore();

    const [user, setUser] = useState<IUserStore | null>(null);

    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [showVideo, setShowVideo] = useState<boolean>(false);
    const [showImprovements, setShowImprovements] = useState<boolean>(false);

    const forwardedActor = (location.state as { executionActor?: ForwardedExecutionActor | null } | null)?.executionActor ?? null;
    const executionActorIds = Array.from(new Set([
        ...(executionsUIStore.execution?.actorIds?.slice() ?? []),
        ...(executionsUIStore.execution?.steps?.map(step => step.actorId ?? '') ?? []),
        ...(executionsUIStore.execution?.events?.map(event => event.actorId ?? '') ?? []),
    ].filter((actorId): actorId is string => Boolean(actorId))));
    const executionActorName = executionActorIds
        .map(actorId => executionsGridUIStore.actorLabels[actorId])
        .filter((actorLabel): actorLabel is string => Boolean(actorLabel))
        .join(', ');
    const resolvedExecutionActorId = executionsGridUIStore.analyticsActorId
        ?? forwardedActor?.actorId
        ?? (executionActorIds.length === 1 ? executionActorIds[0] : null);

    const executionUserName = getExecutionUserName(executionsUIStore.execution?.additionalData);
    const executionUserRole = getExecutionUserRole(executionsUIStore.execution?.additionalData);
    const fallbackUserName = executionsUIStore.executionsUserName ?? forwardedActor?.displayName ?? executionActorName ?? null;
    const fallbackUserRole = executionsUIStore.executionsUserRole ?? forwardedActor?.roleName ?? null;

    useEffect(() =>{
        const routeUserId = executionsGridUIStore.analyticsEntityId ? null : (id ?? null);
        const targetUserId = executionsUIStore.executionsUserId
            ?? routeUserId
            ?? resolvedExecutionActorId;

        if (!targetUserId || ((executionUserName || fallbackUserName) && (executionUserRole || fallbackUserRole))) {
            setUser(null);

            return;
        }

        const loadUser = async () => {
            const result = await userUIStore.loadUser({ id: targetUserId });

            if(result?.success){
                setUser(result.data);

                return;
            }

            setUser(null);
        };

        void loadUser();
    }, [executionUserName, executionUserRole, fallbackUserName, fallbackUserRole, resolvedExecutionActorId, executionsGridUIStore.analyticsEntityId, executionsUIStore.executionsUserId, id, userUIStore])

    const loadVideo = async (videoSrc: string) => {
        try {
            const seconds = await getVideoDurationSeconds(videoSrc);
            setVideoDuration(seconds);
        } catch {
            setVideoDuration(0);
        }
    }

    const resolvedMediaUrl = useResolvedMediaUrl(executionsUIStore.execution?.mediaUrl, showVideo);

    useEffect(() => {
        if (!showVideo || !resolvedMediaUrl) {
            return;
        }

        void loadVideo(resolvedMediaUrl);
    }, [resolvedMediaUrl, showVideo]);

    const closeExecution = () => {
        executionsUIStore.clearSelectedExecutionId();
        executionsGridUIStore.setParams({ page: 0 });
        // Reset only the current grading view — keep per-session drafts so returning
        // to a session restores in-progress (unsaved) grading changes.
        gradingsUIStore.resetCurrentGrading();
        if (location.state?.fromUserInsights) {
            navigate(`/${RoutePaths.users}/analytics/${id}/insights`);

            return;
        }
        if (location.state?.fromProcedureInsights) {
            const insightsId = location.state?.insightsId ?? id;
            navigate(`/${RoutePaths.procedures}/analytics/${insightsId}/insights`);

            return;
        }
        if (location.state?.fromAnalytics) {
            navigate(`/${RoutePaths.analytics}`);

            return;
        }
        const listPath = location.pathname.replace(/\/executions\/[^/]+$/, '');
        navigate(listPath);
    };

    return (
        <Observer>
            {() => {
                const data = executionsUIStore.execution;
                const executionStatus = getExecutionStatusMeta(data ?? {}, theme);
                const gradingSummary = data?.id ? executionsGridUIStore.gradingSummaries[data.id] : undefined;
                const gradingStatusMeta = getGradingStatusMeta(gradingSummary, theme.isDark);

                const additionalDataArray = executionsUIStore.additionalDataItems;
                const improvementSuggestions = gradingsUIStore.groupedBehaviours?.notes ?? [];
                const hasVideo = Boolean(data?.hasMedia && data?.mediaUrl);
                const hasImprovementSuggestions = improvementSuggestions.length > 0;
                const sidebarInfoItems = [
                    {
                        key: 'User',
                        value: executionUserName ?? fallbackUserName ?? (`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || '—'),
                    },
                    {
                        key: 'User Role',
                        value: executionUserRole ?? fallbackUserRole ?? (user?.roles.find(u => u.id == user.roleId.toLocaleString())?.name ?? '—'),
                    },
                    ...additionalDataArray,
                ];

                const executionDuration = getDurationSeconds(data?.duration);

                return (
                    <div className={styles.mainWrapper}>
                        {executionsUIStore.status.isDone &&
                                <>
                                    <div className={styles.headerWraper}>
                                        <div className={styles.imageContainer}>
                                            <img src={BackIcon} onClick={closeExecution}/>
                                        </div>
                                        <div className={styles.executionDateContainer}>
                                            <b>
                                                {`${data ? new Date(data.startTime!).toLocaleDateString() : ''}`}
                                            </b>
                                            <p>
                                                {data ? new Date(data.startTime!).toLocaleTimeString() : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div className={styles.executionTitleWrapper}>
                                        <b>
                                            {data?.entityName ?? 'No Entity Name'}
                                        </b>
                                        {data?.entityVersion && (
                                            <span className={styles.executionVersionSubtle}>
                                                {`Version ${data.entityVersion}`}
                                            </span>
                                        )}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                            {data?.executionMode && (
                                                <div className={styles.executionModeChip}>
                                                    {data.executionMode}
                                                </div>
                                            )}
                                            {gradingStatusMeta && (
                                                <div
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        padding: '2px 12px',
                                                        minHeight: 24,
                                                        borderRadius: 999,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        color: gradingStatusMeta.color,
                                                        backgroundColor: gradingStatusMeta.backgroundColor,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.35,
                                                    }}
                                                >
                                                    {gradingStatusMeta.label}
                                                </div>
                                            )}
                                        </div>
                                        <p>
                                            {data?.entityDescription ?? 'No Description'}
                                        </p>
                                    </div>
                                    <div className={styles.executionStatusWrapper}>
                                        <div className={styles.executionStatusContainer}
                                            style={{
                                                borderColor: executionStatus.color,
                                            }}>
                                            <div className={styles.executionStatusLabel}>
                                                <b>
                                                    {executionStatus.title}
                                                </b>
                                                <p>
                                                    {executionStatus.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.userDataWrapper}>
                                        <div className={styles.additionalDataSection}>
                                            <div className={styles.infoGrid} role="list">
                                                {sidebarInfoItems.map(item => (
                                                    <div
                                                        key={`${item.key}`}
                                                        className={styles.infoCard}
                                                        role="listitem"
                                                    >
                                                        <span className={styles.infoLabel}>
                                                            {formatAdditionalDataLabel(item.key)}
                                                        </span>
                                                        <span className={styles.infoValue} title={item.value}>
                                                            {item.value}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {hasVideo &&
                                    <div className={styles.videoWrapper}>
                                        <div className={styles.videoHeader} onClick={() => {setShowVideo(!showVideo); setShowImprovements(false)}}>
                                            <div className={styles.videoHeaderLabel}>
                                                <img src={VideoIcon}/>
                                                <Typography variant="h6" fontSize={20}>
                                                    Video Recording
                                                </Typography>
                                            </div>
                                            <img src={showVideo ? UpIcon : DownIcon}/>
                                        </div>
                                        {showVideo && resolvedMediaUrl &&
                                            <div className={styles.videoContainer}>
                                                <VideoPlayer videoSrc={resolvedMediaUrl} duration={videoDuration ? videoDuration : executionDuration}/>
                                            </div>
                                        }
                                    </div>
                                    }
                                    {hasImprovementSuggestions &&
                                    <div className={styles.improvementWrapper}>
                                        <div className={styles.improvementHeader} onClick={() => {setShowImprovements(!showImprovements); setShowVideo(false)}}>
                                            <div className={styles.improvementHeaderLabel}>
                                                <img src={ImprovementIcon}/>
                                                <Typography variant="h6" fontSize={20}>
                                                    Improvement suggestions
                                                </Typography>
                                            </div>
                                            <img src={showImprovements ? UpIcon : DownIcon}/>
                                        </div>
                                        {showImprovements &&
                                            <div className={styles.improvementsContainer}>
                                                {improvementSuggestions.map(note => (
                                                    <div key={note.compId}>{note.text}</div>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                    }
                                </>
                        }
                    </div>
                )
            }}
        </Observer>
    )
}